import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  PlusIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import ProductVariants from '../components/products/ProductVariants';
import BulkOperations from '../components/products/BulkOperations';
import { useToast } from '../hooks/useToast';
import { useCompany } from '../contexts/CompanyContext';
import PageHeader from '../components/layout/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { CubeIcon } from '@heroicons/react/24/outline';

// Tipos
interface ProductVariant {
  id: string;
  sku: string;
  name: string;
  stock_quantity: number;
  price_adjustment: string;
  attributes: Record<string, string>;
  final_price: string;
  is_active?: boolean;
}

interface Product {
  id: string;
  name: string;
  reference: string;
  category: string;
  brand?: string;
  base_price: number;
  total_stock: number;
  variants: ProductVariant[];
  is_active: boolean;
  status: string;
  product_type?: 'simple' | 'variable';
  created_at: string;
  updated_at: string;
  images?: Array<{ id: string; url: string; is_primary?: boolean }>;
  // Campos de completude do cadastro
  weight?: number;
  ncm?: string;
  ean?: string;
  // Metadados de integração
  integrations?: {
    shoppub?: { status: string; external_id?: string };
    tiny?: { status: string; external_id?: string };
  };
  platform_integrations?: Array<{
    platform_name: string;
    platform_slug: string;
    icon_url: string;
    color: string;
    is_source: boolean;
  }>;
}

interface ProductFilters {
  search: string;
  status: string;
  integration_status: string;
  has_variants: string;
  has_images: string;
}

const ProductsImproved: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { currentCompany, isLoading: companyLoading } = useCompany();

  // Estados principais
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados de filtros
  const [filters, setFilters] = useState<ProductFilters>({
    search: '',
    status: '',
    integration_status: '',
    has_variants: '',
    has_images: '',
  });
  
  // Estados de view
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list'); // list é melhor para variações
  const [showFilters, setShowFilters] = useState(false);
  
  // Estados de seleção e bulk operations
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50); // Aumentado de 20 para 50
  const [totalCount, setTotalCount] = useState(0);

  // Carregar produtos da API
  const loadProducts = async () => {
    if (!currentCompany) {
      console.log('Sem empresa, não carregando produtos');
      return;
    }

    console.log('Carregando produtos para empresa:', currentCompany.id);

    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');

      // Adicionar parâmetros de filtro
      let url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/products/products/?company_id=${currentCompany.id}&page=${currentPage}&per_page=${pageSize}`;

      if (filters.search) {
        url += `&search=${encodeURIComponent(filters.search)}`;
      }
      if (filters.status) {
        url += `&status=${encodeURIComponent(filters.status)}`;
      }
      if (filters.has_variants) {
        url += `&has_variants=${encodeURIComponent(filters.has_variants)}`;
      }
      if (filters.has_images) {
        url += `&has_images=${encodeURIComponent(filters.has_images)}`;
      }

      console.log('URL de busca:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Company-Id': currentCompany.id,
        },
      });

      const data = await response.json();
      console.log('Resposta dos produtos:', data);
      
      if (response.ok) {
        // Se a resposta for bem-sucedida, processar os dados
        let productsData: any[] = [];
        
        if (data.results !== undefined) {
          // Formato paginado do DRF
          console.log('Formato DRF detectado, produtos:', data.results);
          productsData = data.results || [];
          // Atualizar total de produtos
          if (data.count !== undefined) {
            setTotalCount(data.count);
          }
        } else if (data.data && data.data.results !== undefined) {
          // Formato com wrapper success
          console.log('Formato wrapper detectado, produtos:', data.data.results);
          productsData = data.data.results || [];
          // Atualizar total de produtos
          if (data.data.count !== undefined) {
            setTotalCount(data.data.count);
          }
        } else if (Array.isArray(data)) {
          // Array direto
          console.log('Array direto detectado, produtos:', data);
          productsData = data;
          // Para arrays diretos, o total é o tamanho do array
          setTotalCount(data.length);
        } else {
          // Sem produtos, mas não é erro
          console.log('Nenhum formato detectado, definindo array vazio');
          productsData = [];
          setTotalCount(0);
        }
        
        // Garantir que cada produto tem um array de variants
        const processedProducts = productsData.map(product => ({
          ...product,
          variants: product.variants || [],
          total_stock: product.total_stock || 0,
          base_price: product.base_price || 0,
          is_active: product.status === 'active',
          category: product.category_name || product.category || 'Sem categoria',
          integrations: product.integrations || {}
        }));
        
        setProducts(processedProducts);
        setError(null);
      } else {
        // Só definir erro se a resposta HTTP não for OK
        setError(data.message || 'Erro ao carregar produtos');
      }
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setError('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros aos produtos (apenas busca por texto)
  const applyFilters = () => {
    // Resetar para primeira página
    setCurrentPage(1);
    // Os outros filtros já são aplicados automaticamente via useEffect
    // Aqui só forçamos reload se houver busca por texto
    if (currentCompany) {
      loadProducts();
    }
  };

  // Handlers de filtros
  const handleFilterChange = (key: keyof ProductFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      integration_status: '',
      has_variants: '',
      has_images: '',
    });
    // Resetar página ao limpar filtros
    setCurrentPage(1);
    // Aplicar filtros vazios para recarregar todos os produtos
    setTimeout(() => applyFilters(), 0);
  };

  // Handlers de seleção
  const handleToggleSelect = (productId: string) => {
    setSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProductIds.length === filteredProducts.length) {
      setSelectedProductIds([]);
    } else {
      setSelectedProductIds(filteredProducts.map(p => p.id));
    }
  };

  const clearSelection = () => {
    setSelectedProductIds([]);
  };

  // Handlers de bulk operations
  const handleBulkImportImages = async (productIds: string[]) => {
    try {
      addToast({
        type: 'info',
        title: `Atualizando imagens de ${productIds.length} produto(s)...`
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/products/import-images-direct/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')!).state.accessToken : ''}`,
        },
        body: JSON.stringify({
          product_ids: productIds,
          force: true
        }),
      });

      const data = await response.json();

      if (data.success) {
        addToast({
          type: 'success',
          title: `${data.scheduled} atualizacoes de imagens agendadas`,
          message: 'As imagens serao processadas em background'
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro ao agendar atualizacao de imagens',
          message: data.message || 'Erro desconhecido'
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar imagens:', error);
      addToast({
        type: 'error',
        title: 'Erro ao conectar com o servidor',
        message: 'Verifique sua conexao e tente novamente'
      });
    }
  };

  // Handler para atualizar variação individual
  const handleUpdateVariant = async (variantId: string, updates: { price?: number; stock?: number }) => {
    try {
      const response = await fetch(`/api/v1/products/variants/${variantId}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();
      
      if (data.success) {
        addToast({ type: 'success', title: 'Variação atualizada com sucesso' });
        loadProducts(); // Recarregar produtos
      } else {
        addToast({ type: 'error', title: 'Erro ao atualizar variação' });
      }
    } catch (error) {
      addToast({ type: 'error', title: 'Erro ao atualizar variação' });
    }
  };

  // Carregar produtos quando empresa mudar ou filtros/página mudarem
  useEffect(() => {
    if (currentCompany) {
      loadProducts();
    }
  }, [currentCompany?.id, currentPage, filters.search, filters.status, filters.has_variants, filters.has_images, filters.integration_status]);

  // Definir produtos filtrados (agora só copia os produtos do backend)
  useEffect(() => {
    setFilteredProducts(products);
  }, [products]);

  // Render
  if (companyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Carregando empresa...</p>
        </div>
      </div>
    );
  }

  if (!currentCompany) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Nenhuma empresa encontrada
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Não foi possível carregar os dados da empresa.
          </p>
        </div>
      </div>
    );
  }

  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando produtos...</p>
        </div>
      </div>
    );
  }

  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Carregando produtos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="mt-2 text-sm font-medium text-gray-900">Erro ao carregar produtos</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <Button onClick={loadProducts} className="mt-4">
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Produtos — ${currentCompany.name}`}
        subtitle={`${filteredProducts.length} de ${products.length} produtos`}
        breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'Produtos' }]}
        actions={
          <Button
            onClick={() => navigate('/products/create')}
            className="flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Novo Produto</span>
          </Button>
        }
      />

      <div className="px-8 py-6">
      {/* Filtros e controles */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between gap-4">
          {/* Área de busca (esquerda) */}
          <div className="flex-1 max-w-2xl">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
              <Input
                type="text"
                placeholder="Buscar produtos, SKUs..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                className="pl-10 pr-3 py-2 w-full"
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
          </div>

          {/* Botões de ação (direita) */}
          <div className="flex items-center gap-2">
            {/* Botão Buscar */}
            <Button
              variant="secondary"
              onClick={applyFilters}
              disabled={loading}
              className="flex items-center px-4 py-2 whitespace-nowrap"
            >
              🔍 Buscar
            </Button>
            {/* Botão de filtros */}
            <Button
              variant="secondary"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-2"
            >
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
              <span>Filtros</span>
            </Button>

            {/* Seleção múltipla */}
            {filteredProducts.length > 0 && (
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedProductIds.length === filteredProducts.length}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  Selecionar todos
                </span>
              </label>
            )}

            {/* Controles de visualização */}
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md ${
                viewMode === 'list'
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <ListBulletIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md ${
                viewMode === 'grid'
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Filtros expandidos */}
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 pt-4 border-t border-gray-200">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Status - Todos</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>

            <select
              value={filters.has_variants}
              onChange={(e) => handleFilterChange('has_variants', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Variações - Todos</option>
              <option value="yes">Com variações</option>
              <option value="no">Sem variações</option>
            </select>

            <select
              value={filters.has_images}
              onChange={(e) => handleFilterChange('has_images', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Imagens - Todos</option>
              <option value="yes">Com imagens</option>
              <option value="no">Sem imagens</option>
            </select>

            <Button
              variant="secondary"
              onClick={clearFilters}
              size="sm"
            >
              Limpar
            </Button>
          </div>
        )}
      </div>

      {/* Lista de produtos */}
      <div className="space-y-4">
        {(() => {
          // Backend já retorna produtos paginados, não fazer paginação local
          if (filteredProducts.length === 0) {
            const hasFilters = filters.search || Object.values(filters).some(f => f);
            return (
              <EmptyState
                icon={<CubeIcon className="w-10 h-10" />}
                title="Nenhum produto encontrado"
                description={
                  hasFilters
                    ? 'Tente ajustar os filtros de busca para encontrar o que procura.'
                    : 'Importe produtos de uma plataforma ou crie o primeiro manualmente.'
                }
                action={
                  !hasFilters ? (
                    <Button onClick={() => navigate('/products/create')}>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Novo Produto
                    </Button>
                  ) : undefined
                }
              />
            );
          }

          return filteredProducts.map((product) => (
            <ProductVariants
              key={product.id}
              product={product}
              onUpdateVariant={handleUpdateVariant}
              isSelected={selectedProductIds.includes(product.id)}
              onToggleSelect={handleToggleSelect}
            />
          ));
        })()}
      </div>

      {/* Bulk Operations */}
      <BulkOperations
        selectedProductIds={selectedProductIds}
        selectedProducts={products.filter(p => selectedProductIds.includes(p.id)).map(p => ({
          id: p.id,
          images: p.images || []
        }))}
        onBulkImportImages={handleBulkImportImages}
        onClearSelection={clearSelection}
      />

      {/* Paginação */}
      {totalCount > pageSize && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{Math.min((currentPage - 1) * pageSize + 1, totalCount)}</span> até{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> de{' '}
                <span className="font-medium">{totalCount}</span> produtos
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Anterior</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                  </svg>
                </button>

                <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                  Página {currentPage} de {Math.max(1, Math.ceil(totalCount / pageSize))}
                </span>

                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Próximo</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProductsImproved;